import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface CallDetail {
  id: string;
  direction: 'inbound' | 'outbound';
  status: string;
  date: string;
  duration: number;
  contactName: string | null;
  contactId: string | null;
  dealId: string | null;
  dealName: string | null;
  phoneNumber: string | null;
}

export function useUserCallDetails(
  userId: string | null,
  dateRange?: { from: Date; to: Date },
  direction?: 'inbound' | 'outbound' | null
) {
  const [calls, setCalls] = useState<CallDetail[]>([]);
  const [loading, setLoading] = useState(false);

  const loadDetails = useCallback(async () => {
    if (!userId) {
      setCalls([]);
      return;
    }
    setLoading(true);

    try {
      // Get user's Twilio number and auth_user_id
      const { data: user, error: userError } = await supabase
        .from('app_users')
        .select('twilio_phone_number, auth_user_id')
        .eq('id', userId)
        .maybeSingle();

      if (userError) {
        console.error('Error fetching user:', userError);
        setCalls([]);
        setLoading(false);
        return;
      }

      if (!user?.twilio_phone_number) {
        setCalls([]);
        setLoading(false);
        return;
      }

      // Build separate queries based on direction to avoid complex OR
      let allCalls: any[] = [];

      const baseSelect = `
        id, direction, status, duration_seconds, created_at,
        from_e164, to_e164, contact_id, deal_id,
        contact:contacts!contact_calls_contact_id_fkey(first_name, last_name),
        deal:deals(id, name),
        recordings:call_recordings(duration_seconds)
      `;

      // Helper to apply date filters
      const applyDateFilters = (query: any) => {
        if (dateRange?.from) {
          query = query.gte('created_at', dateRange.from.toISOString());
        }
        if (dateRange?.to) {
          query = query.lte('created_at', dateRange.to.toISOString());
        }
        return query;
      };

      if (direction === 'outbound' || direction === null) {
        // Outbound calls
        // @ts-ignore - Supabase type instantiation too deep for nested joins
        let outboundQuery = supabase
          .from('calls')
          .select(baseSelect)
          .eq('channel', 'voice')
          .in('direction', ['outbound', 'outbound-api', 'outbound-dial'])
          .eq('from_e164', user.twilio_phone_number)
          .order('created_at', { ascending: false })
          .limit(2000);

        outboundQuery = applyDateFilters(outboundQuery);
        const { data: outboundCalls } = await outboundQuery;
        if (outboundCalls) allCalls = [...allCalls, ...outboundCalls];
      }

      if (direction === 'inbound' || direction === null) {
        // Inbound calls - to twilio number
        let inboundQuery1 = supabase
          .from('calls')
          .select(baseSelect)
          .eq('direction', 'inbound')
          .eq('to_e164', user.twilio_phone_number)
          .order('created_at', { ascending: false })
          .limit(250);

        inboundQuery1 = applyDateFilters(inboundQuery1);
        const { data: inboundCalls1 } = await inboundQuery1;
        if (inboundCalls1) allCalls = [...allCalls, ...inboundCalls1];

        // Inbound calls - to client:auth_id
        if (user.auth_user_id) {
          let inboundQuery2 = supabase
            .from('calls')
            .select(baseSelect)
            .eq('direction', 'inbound')
            .eq('to_e164', `client:${user.auth_user_id}`)
            .order('created_at', { ascending: false })
            .limit(250);

          inboundQuery2 = applyDateFilters(inboundQuery2);
          const { data: inboundCalls2 } = await inboundQuery2;
          if (inboundCalls2) allCalls = [...allCalls, ...inboundCalls2];
        }
      }

      // Remove duplicates and sort
      const uniqueCalls = allCalls.filter((call, index, self) => 
        self.findIndex(c => c.id === call.id) === index
      );

      // Sort by date descending
      uniqueCalls.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      // Process results
      const processedCalls: CallDetail[] = uniqueCalls.slice(0, 500).map((call: any) => ({
        id: call.id,
        direction: call.direction?.includes('outbound') ? 'outbound' : 'inbound',
        status: call.status || 'unknown',
        date: call.created_at,
        duration: Math.max(
          call.duration_seconds || 0,
          (call.recordings?.reduce((max: number, r: any) => Math.max(max, r.duration_seconds || 0), 0) || 0)
        ),
        contactName: call.contact 
          ? `${call.contact.first_name || ''} ${call.contact.last_name || ''}`.trim() || null
          : null,
        contactId: call.contact_id,
        dealId: call.deal_id,
        dealName: call.deal?.name || null,
        phoneNumber: call.direction?.includes('outbound') ? call.to_e164 : call.from_e164,
      }));

      setCalls(processedCalls);
    } catch (error) {
      console.error('Error fetching user calls:', error);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [userId, dateRange?.from?.toISOString(), dateRange?.to?.toISOString(), direction]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  return { calls, loading, refresh: loadDetails };
}
